<template>
  <div
    class="pb-2 flex flex-col overflow-hidden relative shadow-[8px_0px_25px_0px_rgba(0,0,0,0.2)]"
    :class="defaultColorClass"
  >
    <TheMainFeatureOfTheApp
      class="absolute top-0 w-full h-auto hidden lg:block"
      preserve-aspect-ratio="xMaxYMax"
      aria-hidden="true"
    />
    <div class="hidden lg:flex items-center px-2 xl:px-4 py-1 xl:py-2 z-10">
      <h2 class="font-bold text-h5 xl:text-h4 grow">Commentaires</h2>
    </div>
    <div class="flex flex-col overflow-y-auto px-1.5 lg:px-2 xl:px-4 pt-2 lg:pt-1 grow lg:h-0" ref="commentsList">
      <div>
        <div v-for="item in items" :key="item.id" class="mb-1" ref="commentItems" :id="getItemId(item)">
          <div
            v-if="item.displayType === 'comment'"
            class="flex flex-col px-2 py-1 rounded-xl rounded-br-none text-sm"
            :class="{
              'bg-black/10': updating?.id !== item.id,
              'bg-black/20': updating?.id === item.id,
            }"
          >
            <div class="flex items-start">
              <div class="text-grey-200">
                <div class="flex gap-1">
                  <span>
                    {{ item.author.firstName }} <span class="uppercase">{{ item.author.lastName }}</span>
                  </span>
                  <span class="text-grey-200">
                    {{ formatDate(item.creationDate) }}
                  </span>
                </div>
                <div
                  class="block whitespace-nowrap overflow-hidden text-ellipsis my-1"
                  v-if="isPrivateMessageEnabled && item.target"
                >
                  À
                  <span class="inline-flex gap-1v" v-if="item.target.firstName">
                    <span>{{ item.target.firstName }}</span>
                    <span class="uppercase">{{ item.target.lastName }}</span>
                  </span>
                  <span v-else>
                    <span>{{ item.target.name }}</span>
                  </span>
                </div>
              </div>

              <div class="ml-auto flex flex-col justify-end items-end">
                <PBadge
                  size="sm"
                  type="custom"
                  v-if="isPrivateMessageEnabled && item.target"
                  class="ml-auto uppercase bg-black text-white mb-1v"
                >
                  Privé
                </PBadge>
                <div v-if="!readonly && !item.frozen && item.author.id === authStore.currentUser?.id">
                  <button
                    @click="setupUpdate(item)"
                    v-if="item.type === LetterFileCommentType.TEXT"
                    :data-sentry-id="`UpdateCommentBtn-${item.id}`"
                  >
                    <span class="sr-only">éditer le commentaire "{{ item.text }}"</span>
                    <OhVueIcon
                      :name="updating?.id === item.id ? closeIcon : editIcon"
                      scale="0.9"
                      class="text-blue-france-sun-113 cursor-pointer mr-1"
                    />
                  </button>
                  <button @click="onDelete(item)" :data-sentry-id="`DeleteCommentBtn-${item.id}`">
                    <span class="sr-only">supprimer le commentaire "{{ item.text }}"</span>
                    <OhVueIcon :name="deleteIcon" scale="0.9" class="text-blue-france-sun-113 cursor-pointer" />
                  </button>
                </div>
              </div>
            </div>
            <pre v-if="item.type === LetterFileCommentType.TEXT" class="whitespace-pre-wrap font-marianne">{{
              item.text
            }}</pre>
            <AudioPlayerCustom
              v-else
              :src="JSON.parse(item.text).link"
              :model-value="playedMap[item.id]?.value || 0"
              @update:model-value="onTimeUpdate(item, $event)"
            />
          </div>

          <div v-else class="text-sm flex items-center ml-2">
            <span v-if="item.author" class="text-grey-200 mr-1v whitespace-nowrap">
              {{ item.author.firstName }} <span class="uppercase">{{ item.author.lastName }}</span
              >:
            </span>
            <PTooltip
              :label="item.name"
              activatorWrapperClasses="w-full whitespace-nowrap overflow-hidden text-ellipsis"
              class="grow w-0"
              :delay="300"
              :offset="5"
            >
              <span class="text-grey-50">copie à {{ item.name }}</span>
            </PTooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="lg:hidden" :style="{ 'padding-bottom': `${commentActionsHeight + 100}px` }"></div>
    <div
      v-if="!readonly"
      class="fixed bottom-0 left-0 w-full lg:static shadow-top pb-1.5 lg:pb-0"
      :class="defaultColorClass"
      ref="commentActions"
    >
      <div class="px-1.5 lg:px-2 xl:px-4 pt-1v lg:pt-2 overflow-hidden">
        <Transition mode="out-in" name="fade">
          <div v-if="uploading" class="bg-grey-975 p-3 flex flex-col gap-1 bg-blue-france-su">
            <span>Envoi du message en cours ...</span>
            <Progressbar />
          </div>
          <AudioRecorder v-else-if="audioRecorder.state.value !== 'inactive'" :on-data="onAudioMsg" />
          <div v-else>
            <PModal title="Le destinataire n'a pas accès au parapheur." @close="onMsgModalClose">
              <template #default>Souhaitez-vous l'ajouter en copie afin de lui en donner l'accès ?</template>
              <template #actions="{ close }">
                <div class="flex justify-end gap-1">
                  <PButton mode="secondary" @click="resetCommentTarget(close)">Annuler</PButton>
                  <PButton @click="addCCAndSend(close)">Ajouter et envoyer</PButton>
                </div>
              </template>
              <template #activator="{ open: openCCModal }">
                <div class="flex items-center">
                  <CCUsersModal :letter-file="letterFile!" :readonly="readonly" @close="refreshLetterFile" />
                  <PTextarea
                    label=""
                    ref="input"
                    class="grow [&_.p-form-message]:hidden border-b-blue-france-sun-113"
                    v-model="commentText"
                    placeholder="Ajouter un commentaire"
                    data-sentry-id="CommentTextArea"
                    aria-label="Ajouter un commentaire"
                    :class="{ italic: !commentText }"
                    :rows="3"
                    :error-messages="commentTextErrors"
                    :style="{ 'max-height': '25vh' }"
                    @keyup.esc="resetForm"
                    @keyup.alt.enter.stop="submitComment(openCCModal)"
                    :append-icon="canRecordPrivateMsg ? micIcon : undefined"
                    append-icon-label="Enregistrer un message vocal privé"
                    @click:append-icon="audioRecorder.start()"
                  />
                </div>
                <div class="mt-1.5 pl-6 flex">
                  <PModal
                    title="Commentaire privé"
                    v-if="isPrivateMessageEnabled"
                    @open="tmpCommentTarget = commentTarget ? [commentTarget] : []"
                    @close="onMsgModalClose"
                  >
                    <template #default>
                      <div v-if="!commentTarget">
                        <SelectUser
                          v-model="tmpCommentTarget"
                          ref="selectPrivateMsgTargetEl"
                          class="[&_.p-form-message]:hidden"
                        ></SelectUser>
                      </div>
                      <div v-else>À destination de : {{ commentTarget.fullName }}</div>
                    </template>
                    <template #actions="{ close }">
                      <div v-if="!commentTarget">
                        <FavoriteUsers v-if="isFavoriteUsersEnabled" @select="onSelectCommentTarget($event, close)" />
                        <div class="flex justify-end gap-1">
                          <PButton mode="secondary" @click="close" data-sentry-id="CommentCancelPrivateBtn">
                            Annuler</PButton
                          >
                          <PButton @click="setupPrivateCommentTarget(close)" data-sentry-id="CommentValidatePrivateBtn">
                            Valider
                          </PButton>
                        </div>
                      </div>
                    </template>
                    <template #activator="{ open: showDestinataire }">
                      <div class="flex justify-between items-center gap-1v w-full pr-1.5 lg:pr-4">
                        <PToggleSwitch
                          label="Privé"
                          :model-value="commentTarget !== null"
                          :show-state="false"
                          label-position="left"
                          @update:model-value="togglePrivateMessage(showDestinataire)"
                          :disabled="!!updating || audioRecorder.state.value !== 'inactive'"
                          data-sentry-id="CommentPrivateToggle"
                        />
                        <button
                          @click="showDestinataire"
                          v-if="commentTarget"
                          class="text-blue-france-sun-113 text-sm ml-1 hover:text-blue-france-sun-113-hover"
                          data-sentry-id="CommentSeePrivateTargetBtn"
                        >
                          Voir le destinataire
                        </button>
                      </div>
                    </template>
                  </PModal>
                  <PButton
                    @click="submitComment(openCCModal)"
                    data-sentry-id="CommentSendBtn"
                    title="Envoyer le commentaire"
                    aria-label="Envoyer le commentaire"
                    class="p-1!"
                  >
                    <OhVueIcon :name="sendIcon" :scale="1.25" />
                  </PButton>
                  <PButton
                    v-if="canRecordPrivateMsg"
                    @click="audioRecorder.start()"
                    class="sr-only focus:not-sr-only"
                    data-sentry-id="AudioRecorderStartBtn"
                  >
                    Enregistrer un message vocal privé
                  </PButton>
                </div>
              </template>
            </PModal>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRelatedUsersApi } from "@/app/http/letter-file-related-users.api";
import { useLetterFileCCUserStore } from "@/app/stores/cc-user.store";
import { useAudioRecorder } from "@/audio-recorder/composables/audio-recorder.ts";
import { subject } from "@casl/ability";
import {
  AlertDuration,
  AlertType,
  type Nullable,
  PBadge,
  PButton,
  PModal,
  PTextarea,
  PToggleSwitch,
  PTooltip,
  timeout,
  useAlertManager,
  useIcons,
} from "@pasta/box-v2";
import { useAuthStore } from "@pasta/front-auth-v2";
import { useElementSize } from "@vueuse/core";
import dayjs from "dayjs";
import { OhVueIcon } from "oh-vue-icons";
import { RiCloseLine, RiDeleteBin6Line, RiEditFill, RiMicLine, RiSendPlane2Line } from "oh-vue-icons/icons";
import { computed, inject, nextTick, onMounted, onUnmounted, type Ref, ref, watch } from "vue";
import { onBeforeRouteLeave, routeLocationKey } from "vue-router";
import AudioPlayerCustom from "../../../audio-recorder/components/AudioPlayerCustom.vue";
import AudioRecorder from "../../../audio-recorder/components/AudioRecorder.vue";
import FavoriteUsers from "../../components/FavoriteUsers.vue";
import Progressbar from "../../components/Progressbar.vue";
import SelectUser from "../../components/SelectUser.vue";
import TheMainFeatureOfTheApp from "../../components/TheMainFeatureOfTheApp.vue";
import { useNavigationChecker } from "../../composable/navigation-checker";
import { useNotifyAsyncOperation } from "../../composable/useAsyncOperation";
import { useLetterFileColor } from "../../composable/useLetterFileColor";
import { type AgentsDirectoryAutocompleteItem } from "../../dto/agents-directory.dto";
import { type LetterFileCCUserDto } from "../../dto/letter-file-cc-user.dto";
import {
  type LetterFileCommentAudioText,
  type LetterFileCommentDto,
  LetterFileCommentType,
} from "../../dto/letter-file-comment.dto";
import { useFileApi } from "../../http/file.api";
import { useLetterFileCommentApi } from "../../http/letter-file-comment.api";
import { useLetterFileStore } from "../../stores/letter-file.store";
import { useSimpleFeatureStore } from "../../stores/simple-features.store";
import { FormOperation } from "../../types";
import CCUsersModal from "./Flux/CCUsersModal.vue";
import { wrapUnlessSessionExpired } from "./utils";

interface Props {
  readonly: boolean;
}

defineProps<Props>();
const letterFileStore = useLetterFileStore();
const letterFile = computed(() => letterFileStore.getCurrent());
const commentsApi = useLetterFileCommentApi();
const notifyAsyncOperation = useNotifyAsyncOperation();
const { defaultColorClass } = useLetterFileColor();
const simpleFeatureStore = useSimpleFeatureStore();
const isPrivateMessageEnabled = computed(() => simpleFeatureStore.privateMessage?.enabled || false);
const isPrivateAudioMessageEnabled = computed(
  () => isPrivateMessageEnabled.value && (simpleFeatureStore.privateMessage?.audio.enabled || false)
);
const isFavoriteUsersEnabled = computed(() => simpleFeatureStore.favoriteUsers.enabled);

const tmpCommentTarget = ref<AgentsDirectoryAutocompleteItem[]>([]);
const commentTarget = ref<Nullable<AgentsDirectoryAutocompleteItem>>(null);
const selectPrivateMsgTargetEl = ref<typeof SelectUser | null>(null);
const ccUserStore = useLetterFileCCUserStore();
const alertManager = useAlertManager();

const navigationChecker = useNavigationChecker();
if (inject(routeLocationKey, null)) {
  onBeforeRouteLeave((_to, _from) => {
    try {
      navigationChecker.check();
    } catch {
      return false;
    }
    return true;
  });
}

async function togglePrivateMessage(showDestinataire: () => void) {
  if (!commentTarget.value) {
    showDestinataire();
    await nextTick();
    selectPrivateMsgTargetEl.value?.focus();
  } else {
    commentTarget.value = null;
  }
}

async function setupPrivateCommentTarget(closePrivateRecipientModal: () => void) {
  commentTarget.value = tmpCommentTarget.value.at(0);
  closePrivateRecipientModal();
}

async function resetCommentTarget(closeCCModal: () => void) {
  tmpCommentTarget.value = [];
  commentTarget.value = null;
  alertManager.createAlert({
    type: AlertType.WARNING,
    duration: AlertDuration.MEDIUM,
    message: "Le commentaire n’a pas été envoyé.",
  });

  closeCCModal();
}

async function addCCAndSend(closeCCModal: () => void) {
  await notifyAsyncOperation(async () => {
    await ccUserStore.create({
      mail: commentTarget.value!.mail,
      letterFile: letterFile.value!.id,
    });
  }, FormOperation.CC_USER);
  alertManager.createAlert({
    type: AlertType.SUCCESS,
    duration: AlertDuration.SHORT,
    message: "Le destinataire a été ajouté en copie.",
  });

  closeCCModal();
  await sendComment();
}

async function onMsgModalClose() {
  await timeout(200);
  input.value!.mainElement.focus();
}

const comments = computed(() =>
  (letterFile.value?.comments || []).filter(comment => {
    const isPrivate = !!comment.target;
    const isText = comment.type === LetterFileCommentType.TEXT;
    const isAudio = comment.type === LetterFileCommentType.AUDIO;
    return (
      !isPrivate ||
      (isPrivate && isText && isPrivateMessageEnabled.value) ||
      (isPrivate && isAudio && isPrivateAudioMessageEnabled.value)
    );
  })
);
const ccUsers = computed(() => letterFile.value?.ccUsers || []);
const items = computed(() => {
  return [...comments.value, ...ccUsers.value]
    .map(item => {
      if ((item as any).text) {
        return subject("LetterFileComment", {
          ...item,
          displayType: "comment",
        }) as LetterFileCommentDto & { displayType: "comment" };
      }
      return subject("LetterFileCCUser", {
        ...item,
        displayType: "ccUser",
      }) as LetterFileCCUserDto & { displayType: "ccUser" };
    })
    .sort((a, b) => {
      return (new Date(a.creationDate) as any) - (new Date(b.creationDate) as any);
    });
});

const commentsList = ref<Nullable<HTMLDivElement>>(null);
const commentItems = ref<HTMLDivElement[]>([]);

function scrollManager() {
  function getItemId(item: { id: number }) {
    return `${item.id}`;
  }

  async function scrollToLastComment() {
    await nextTick();
    if (items.value.length) {
      const lastItemId = getItemId(items.value.at(-1)!);
      commentItems.value
        .find(el => el.id === lastItemId)
        ?.scrollIntoView({
          behavior: "auto",
          block: "end",
        });
    }
  }

  watch(() => items.value.length, scrollToLastComment, { immediate: true });
  const resizeObs = new ResizeObserver(scrollToLastComment);
  onMounted(() => {
    resizeObs.observe(commentsList.value!);
  });
  onUnmounted(() => {
    resizeObs.disconnect();
  });
  return { getItemId };
}

const { getItemId } = scrollManager();

const [deleteIcon, sendIcon, editIcon, closeIcon, micIcon] = useIcons(
  RiDeleteBin6Line,
  RiSendPlane2Line,
  RiEditFill,
  RiCloseLine,
  RiMicLine
);

function formatDate(date: string) {
  return dayjs(date).format("DD/MM/YYYY à HH[h]mm");
}

const { commentText } = useNavigationChecker();
onMounted(() => {
  commentText.value = "";
});
const commentTextErrors = ref<string[]>([]);
const input = ref<{ mainElement: HTMLInputElement }>();

watch(commentText, val => {
  if (val) {
    commentTextErrors.value = [];
  }
});

async function submitComment(openCCModal: () => void) {
  if (!commentText.value) {
    commentTextErrors.value = ["Le commentaire ne peut pas être vide"];
    return;
  }

  if (commentTarget.value && !(await isRelatedToLf(commentTarget.value.mail))) {
    openCCModal();
  } else await sendComment();
}

async function sendComment() {
  await notifyAsyncOperation(async () => {
    if (!updating.value) {
      await commentsApi.create({
        letterFile: letterFile.value!.id,
        text: commentText.value,
        target: commentTarget.value?.mail,
      });
    } else {
      await commentsApi.update(updating.value.id, {
        letterFile: letterFile.value!.id,
        text: commentText.value,
        target: commentTarget.value?.mail,
      });
    }

    commentTarget.value = null;
  }, FormOperation.COMMENT);

  resetForm();
  await fixTextAreaHeight();
  await notifyAsyncOperation(refresh, FormOperation.REFRESH);
}

async function onDelete(
  item: (LetterFileCommentDto | LetterFileCCUserDto) & {
    displayType: "comment" | "ccUser";
  }
) {
  const message = "Êtes vous sûr(e) de vouloir supprimer ce commentaire ?";
  if (confirm(message)) {
    await notifyAsyncOperation(async () => {
      return commentsApi.delete(item.id, letterFile.value!.id);
    }, FormOperation.COMMENT);
  }
  if (item.displayType === "comment" && updating.value?.id === item.id) {
    resetForm();
    await fixTextAreaHeight();
  }
  await notifyAsyncOperation(refresh, FormOperation.REFRESH);
}

async function refresh(force = true) {
  await Promise.all([letterFileStore.fetchCurrent(force).catch(wrapUnlessSessionExpired), timeout(500)]);
}

const updating = ref<Nullable<LetterFileCommentDto>>(null);

async function setupUpdate(comment: LetterFileCommentDto) {
  if (updating.value?.id === comment.id) {
    resetForm();
  } else {
    updating.value = comment;
    commentText.value = updating.value.text;
    if (comment.target) {
      commentTarget.value = {
        fullName: comment.target.name,
        mail: comment.target.mail,
      };
    } else {
      commentTarget.value = null;
    }
  }
  input.value!.mainElement.focus();
  await fixTextAreaHeight();
}

function resetForm() {
  updating.value = null;
  commentText.value = "";
  commentTextErrors.value = [];
  tmpCommentTarget.value = [];
  commentTarget.value = null;
}

async function fixTextAreaHeight() {
  await nextTick();
  input.value!.mainElement.style.height = "";
  input.value!.mainElement.style.height = input.value!.mainElement.scrollHeight + "px";
}

const commentActions = ref<HTMLDivElement | null>(null);
const commentActionsSize = useElementSize(commentActions);
const commentActionsHeight = ref(0);
watch(
  () => commentActionsSize.height.value,
  value => {
    requestAnimationFrame(() => {
      commentActionsHeight.value = value || 0;
    });
  }
);

// Audio
const audioRecorder = useAudioRecorder();
const canRecordPrivateMsg = computed(() => {
  return (
    commentTarget.value &&
    isPrivateAudioMessageEnabled.value &&
    audioRecorder.state.value === "inactive" &&
    audioRecorder.supportsMedia.value
  );
});

const fileApi = useFileApi();
const uploading = ref(false);

async function onAudioMsg(msg: { data: Blob; duration: number }) {
  uploading.value = true;
  await notifyAsyncOperation(async () => {
    const form = new FormData();
    const file = new File([msg.data], "audio-msg.webm");
    form.append("file", file);
    const { id } = await fileApi.uploadFile(form, "upload-audio");

    await commentsApi.create({
      letterFile: letterFile.value!.id,
      text: JSON.stringify({
        fileId: id,
        duration: msg.duration,
      }),
      target: commentTarget.value?.mail,
      type: LetterFileCommentType.AUDIO,
    });
  }, FormOperation.COMMENT);
  await notifyAsyncOperation(refresh, FormOperation.REFRESH);
  resetForm();
  uploading.value = false;
}

function onSelectCommentTarget(user: AgentsDirectoryAutocompleteItem, closeCommentTargetModal: () => void) {
  tmpCommentTarget.value = [user];
  setupPrivateCommentTarget(closeCommentTargetModal);
}

const playedMap: Record<number, Ref<number>> = {};
const authStore = useAuthStore();
const lastSavedPlayedMap: Record<number, number> = {};
watch(
  () => comments.value,
  val => {
    val
      .filter(item => item.type === LetterFileCommentType.AUDIO)
      .forEach(item => {
        const parsed: LetterFileCommentAudioText = JSON.parse(item.text);
        const currentTime = parsed.currentTime ? parsed.currentTime[authStore.currentUser!.id] || 0 : 0;
        playedMap[item.id] = ref(currentTime);
        lastSavedPlayedMap[item.id] = currentTime;
      });
  },
  { immediate: true }
);

async function onTimeUpdate(item: LetterFileCommentDto, value: number) {
  playedMap[item.id].value = value;
  await savePlayed(item, value);
}

async function savePlayed(item: LetterFileCommentDto, value: number) {
  const lastSaved = lastSavedPlayedMap[item.id];
  if (value >= lastSaved + 5 || value <= lastSaved - 5) {
    lastSavedPlayedMap[item.id] = value;
    await commentsApi.putPlayed(item.id, letterFile.value!.id, Math.floor(value));
  }
}

async function refreshLetterFile() {
  await letterFileStore.fetchCurrent();
}

function isRelatedToLf(mail: string): Promise<boolean> {
  return useRelatedUsersApi().isRelated(letterFile.value!.id, mail);
}
</script>

<style>
.shadow-top {
  box-shadow: 0 -4px 6px -1px rgb(0 0 0 / 0.1);
}
</style>
